import {
	type BucketOutput,
	bucketIndexOf,
	closeAndEmitBucket,
	collectRealTimestamps,
	createBucketLayout,
	pushAnchor,
} from "./aggregateSeries.helpers";
import { findLastIndexWhere } from "./binarySearch";
import type { AggregationMode } from "./trendingUrlState";

export interface EnvelopeBand {
	min: [number, number | null][];
	max: [number, number | null][];
}

export interface AggregatedSeries {
	data: [number, number | null][];
	band?: EnvelopeBand;
	decimated: boolean;
	visibleCount: number;
	firstTimestamps?: number[];
	realTimestamps?: number[];
}

export const aggregateSeries = (
	data: [number, number | null][],
	visibleStart: number,
	visibleEnd: number,
	targetPoints: number,
	mode: AggregationMode,
	sourceFirstTimestamps?: number[],
): AggregatedSeries => {
	const firstAt = (index: number) =>
		sourceFirstTimestamps?.[index] ?? data[index]?.[0];

	if (data.length === 0) {
		return { data, decimated: false, visibleCount: 0 };
	}

	const lastIdx = data.length - 1;

	const leftAnchorIdx = findLastIndexWhere(
		data,
		(point) => point[0] < visibleStart,
	);
	const lastVisibleIdx = findLastIndexWhere(
		data,
		(point) => point[0] <= visibleEnd,
	);

	if (lastVisibleIdx < 0) {
		return {
			data: data.slice(0, 1),
			decimated: false,
			visibleCount: 0,
			firstTimestamps: [firstAt(0)],
		};
	}

	const rightAnchorIdx = lastVisibleIdx < lastIdx ? lastVisibleIdx + 1 : -1;

	const hasLeftAnchor = leftAnchorIdx >= 0;
	const hasRightAnchor = rightAnchorIdx >= 0;

	const firstVisibleIdx = leftAnchorIdx + 1;
	const visibleCount = Math.max(0, lastVisibleIdx - leftAnchorIdx);

	const keptFrom = hasLeftAnchor ? leftAnchorIdx : firstVisibleIdx;
	const keptTo = hasRightAnchor ? rightAnchorIdx : lastVisibleIdx;

	if (keptTo < keptFrom) {
		return {
			data: data.slice(keptFrom, keptFrom + 1),
			decimated: false,
			visibleCount,
			firstTimestamps: [firstAt(keptFrom)],
		};
	}

	const isEnvelopeMode = mode === "envelope";

	const layout = createBucketLayout(visibleStart, visibleEnd, targetPoints);

	if (visibleCount <= targetPoints || targetPoints < 4) {
		const slice = data.slice(keptFrom, keptTo + 1);
		const sliceFirstTimestamps: number[] = [];
		for (let i = keptFrom; i <= keptTo; i++) {
			sliceFirstTimestamps.push(firstAt(i));
		}

		return {
			data: slice,
			band: isEnvelopeMode ? { min: slice, max: slice } : undefined,
			decimated: false,
			visibleCount,
			firstTimestamps: sliceFirstTimestamps,
			realTimestamps: collectRealTimestamps({
				points: slice,
				hasLeftAnchor,
				hasRightAnchor,
			}),
		};
	}

	const output: BucketOutput = {
		points: [],
		bandMin: [],
		bandMax: [],
		realTimestamps: [],
		firstTimestamps: [],
	};

	if (hasLeftAnchor) {
		pushAnchor({
			data,
			index: leftAnchorIdx,
			mode,
			firstTimestamp: firstAt(leftAnchorIdx),
			into: output,
		});
	}

	if (visibleCount > 0) {
		let bucketStartIdx = firstVisibleIdx;
		let currentBucket = bucketIndexOf(layout, data[firstVisibleIdx][0]);

		for (let i = firstVisibleIdx + 1; i <= lastVisibleIdx; i++) {
			const bucket = bucketIndexOf(layout, data[i][0]);

			if (bucket !== currentBucket) {
				closeAndEmitBucket({
					data,
					startIdx: bucketStartIdx,
					endIdx: i - 1,
					mode,
					firstTimestamp: firstAt(bucketStartIdx),
					into: output,
				});

				bucketStartIdx = i;
				currentBucket = bucket;
			}
		}

		closeAndEmitBucket({
			data,
			startIdx: bucketStartIdx,
			endIdx: lastVisibleIdx,
			mode,
			firstTimestamp: firstAt(bucketStartIdx),
			into: output,
		});
	}

	if (hasRightAnchor) {
		pushAnchor({
			data,
			index: rightAnchorIdx,
			mode,
			firstTimestamp: firstAt(rightAnchorIdx),
			into: output,
		});
	}

	return {
		data: output.points,
		band: isEnvelopeMode
			? { min: output.bandMin, max: output.bandMax }
			: undefined,
		decimated: true,
		visibleCount,
		firstTimestamps: output.firstTimestamps,
		realTimestamps: output.realTimestamps,
	};
};
